<template>
  <div class="admin-view">
    <div class="view-header">
      <h2>Contact Messages</h2>
      <div class="toolbar">
        <div class="cm-viewtabs">
          <button :class="['cm-tab', { active: view === 'active' }]"   @click="setView('active')">Active</button>
          <button :class="['cm-tab', { active: view === 'archived' }]" @click="setView('archived')">Archived</button>
        </div>
        <select v-model="statusFilter" class="filter-select" @change="reload(0)">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
      </div>
    </div>

    <!-- Loading indicator -->
    <div v-if="loading" class="leads-loading">
      <div class="loading-row" v-for="n in 4" :key="n"></div>
    </div>

    <p v-else-if="loadError" class="load-error">{{ loadError }}</p>
    <div v-else-if="leads.length === 0" class="empty">
      {{ view === 'archived' ? 'No archived messages.' : 'No messages.' }}
    </div>

    <template v-else>
      <!-- Bulk action bar -->
      <div class="cm-bar">
        <label class="cm-selectall">
          <input
            type="checkbox"
            :checked="allSelected"
            :indeterminate.prop="someSelected && !allSelected"
            @change="toggleSelectAll"
          />
          <span v-if="selectedCount === 0">Select page</span>
          <span v-else>{{ selectedCount }} selected</span>
        </label>

        <div class="cm-bar-actions" v-if="selectedCount > 0">
          <button class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkStatus('contacted')">Mark Contacted</button>
          <button class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkStatus('converted')">Mark Converted</button>
          <button class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkStatus('closed')">Close</button>

          <button v-if="view === 'active'"  class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkArchive(true)">Archive</button>
          <button v-else                    class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkArchive(false)">Unarchive</button>

          <template v-if="!bulkDeleteConfirm">
            <button class="btn btn-sm btn-discard" :disabled="bulkBusy" @click="bulkDeleteConfirm = true">Delete selected</button>
          </template>
          <template v-else>
            <span class="discard-confirm-label">Delete {{ selectedCount }} permanently?</span>
            <button class="btn btn-sm btn-discard-confirm" :disabled="bulkBusy" @click="bulkDelete">
              {{ bulkBusy ? 'Deleting…' : 'Yes, delete' }}
            </button>
            <button class="btn btn-sm btn-outline" :disabled="bulkBusy" @click="bulkDeleteConfirm = false">Cancel</button>
          </template>
        </div>

        <!-- Pagination -->
        <div class="cm-pager">
          <label class="cm-pagesize">
            <span>Per page</span>
            <select :value="limit" class="filter-select filter-select-sm" @change="changePageSize($event)">
              <option v-for="n in PAGE_SIZE_OPTIONS" :key="n" :value="n">{{ n }}</option>
            </select>
          </label>
          <span class="cm-pageinfo">{{ pageRangeLabel }}</span>
          <button class="btn btn-sm btn-outline" :disabled="offset === 0 || loading" @click="reload(offset - limit)">‹ Prev</button>
          <button class="btn btn-sm btn-outline" :disabled="offset + limit >= total || loading" @click="reload(offset + limit)">Next ›</button>
        </div>
      </div>

      <div class="cm-list">
        <div
          v-for="lead in leads"
          :key="lead.id"
          class="cm-row"
          :class="{ expanded: expandedId === lead.id, selected: selected.has(lead.id) }"
        >
          <!-- Compact summary row -->
          <div class="cm-summary" @click="toggleExpand(lead.id)">
            <input
              type="checkbox"
              class="cm-check"
              :checked="selected.has(lead.id)"
              @click.stop
              @change="toggleSelect(lead.id)"
            />
            <span class="cm-chevron">{{ expandedId === lead.id ? '▾' : '▸' }}</span>
            <span :class="['status-dot', `dot-${lead.status}`]" :title="lead.status"></span>
            <span class="cm-name">{{ lead.name }}</span>
            <a :href="`mailto:${lead.email}`" class="cm-email" @click.stop>{{ lead.email }}</a>
            <span class="cm-service" v-if="lead.service_type">{{ lead.service_type }}</span>
            <span class="cm-preview" v-if="expandedId !== lead.id">{{ lead.message }}</span>
            <span class="cm-spacer"></span>
            <span :class="['status-badge', `status-${lead.status}`]">{{ lead.status }}</span>
            <small class="cm-date">{{ formatDate(lead.created_at) }}</small>
          </div>

          <!-- Expanded detail panel -->
          <div v-if="expandedId === lead.id" class="cm-detail">
            <p class="cm-message-text" @click="toggleExpand(lead.id)" title="Click to collapse">{{ lead.message }}</p>
            <div class="cm-actions">
              <button
                v-for="s in nextStatuses(lead.status)"
                :key="s.value"
                @click="updateStatus(lead, s.value)"
                class="btn btn-sm btn-outline"
                :disabled="updating === lead.id"
              >{{ s.label }}</button>

              <button
                class="btn btn-sm btn-outline"
                :disabled="updating === lead.id"
                @click="archiveLead(lead, view === 'active')"
              >{{ view === 'active' ? 'Archive' : 'Unarchive' }}</button>

              <!-- Discard -->
              <template v-if="discardConfirmId !== lead.id">
                <button class="btn btn-sm btn-discard" @click.stop="discardConfirmId = lead.id">
                  Discard
                </button>
              </template>
              <template v-else>
                <span class="discard-confirm-label">Delete permanently?</span>
                <button class="btn btn-sm btn-discard-confirm" :disabled="discarding === lead.id" @click.stop="discardLead(lead)">
                  {{ discarding === lead.id ? 'Deleting…' : 'Yes, delete' }}
                </button>
                <button class="btn btn-sm btn-outline" @click.stop="discardConfirmId = null">Cancel</button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAdminApi } from '@/composables/useAdminApi'

interface Lead {
  id: number
  name: string
  email: string
  service_type: string
  message: string
  status: 'new' | 'contacted' | 'converted' | 'closed'
  created_at: string
  archived_at: string | null
}

const PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const PAGE_SIZE_KEY = 'cm_page_size'

const { apiFetch, formatDate } = useAdminApi()

// Restore the user's saved page-size preference (falls back to default).
const savedSize = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || '')
const initialLimit = PAGE_SIZE_OPTIONS.includes(savedSize) ? savedSize : PAGE_SIZE

const leads            = ref<Lead[]>([])
const total            = ref(0)
const limit            = ref(initialLimit)
const offset           = ref(0)
const loadError        = ref('')
const loading          = ref(true)
const view             = ref<'active' | 'archived'>('active')
const statusFilter     = ref('')
const updating         = ref<number | null>(null)
const expandedId       = ref<number | null>(null)
const discardConfirmId = ref<number | null>(null)
const discarding       = ref<number | null>(null)

// Bulk selection
const selected          = ref<Set<number>>(new Set())
const bulkBusy          = ref(false)
const bulkDeleteConfirm = ref(false)

// Every transition except the row's current status.
const ALL_STATUSES: { value: Lead['status']; label: string }[] = [
  { value: 'contacted', label: 'Mark Contacted' },
  { value: 'converted', label: 'Mark Converted' },
  { value: 'closed',    label: 'Close' },
  { value: 'new',       label: 'Reopen' },
]
const nextStatuses = (status: string) =>
  ALL_STATUSES
    .filter(s => s.value !== status)
    .map(s => s.value === 'new' ? { ...s, label: status === 'closed' ? 'Reopen' : 'Reset to New' } : s)

// ── Data loading ─────────────────────────────────────────────────────────────
const reload = async (newOffset = offset.value) => {
  loading.value = true
  loadError.value = ''
  clearSelection()
  expandedId.value = null
  try {
    const params = new URLSearchParams({
      archived: String(view.value === 'archived'),
      limit: String(limit.value),
      offset: String(Math.max(newOffset, 0)),
    })
    if (statusFilter.value) params.set('status', statusFilter.value)
    const res  = await apiFetch(`/admin/contact-leads?${params.toString()}`)
    const data = await res.json()
    if (res.ok) {
      leads.value  = data.leads
      total.value  = data.total
      offset.value = data.offset
      limit.value  = data.limit
    } else {
      loadError.value = data.error
    }
  } catch (e: any) {
    if (e.message !== 'Unauthorized') loadError.value = 'Failed to load messages'
  } finally {
    loading.value = false
  }
}

onMounted(() => reload(0))

const setView = (v: 'active' | 'archived') => {
  if (view.value === v) return
  view.value = v
  reload(0)
}

const changePageSize = (e: Event) => {
  const next = parseInt((e.target as HTMLSelectElement).value)
  if (!PAGE_SIZE_OPTIONS.includes(next) || next === limit.value) return
  limit.value = next
  localStorage.setItem(PAGE_SIZE_KEY, String(next))
  reload(0) // reset to the first page under the new size
}

const pageRangeLabel = computed(() => {
  if (total.value === 0) return '0 of 0'
  const from = offset.value + 1
  const to   = Math.min(offset.value + limit.value, total.value)
  return `${from}–${to} of ${total.value}`
})

// ── Selection (scoped to current page) ───────────────────────────────────────
const selectedCount = computed(() => selected.value.size)
const someSelected  = computed(() => selectedCount.value > 0)
const allSelected   = computed(() =>
  leads.value.length > 0 && leads.value.every(l => selected.value.has(l.id))
)

const toggleSelect = (id: number) => {
  const next = new Set(selected.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selected.value = next
  bulkDeleteConfirm.value = false
}

const toggleSelectAll = () => {
  selected.value = allSelected.value ? new Set() : new Set(leads.value.map(l => l.id))
  bulkDeleteConfirm.value = false
}

const clearSelection = () => {
  selected.value = new Set()
  bulkDeleteConfirm.value = false
}

// ── Single-row actions ───────────────────────────────────────────────────────
const toggleExpand = (id: number) => {
  expandedId.value = expandedId.value === id ? null : id
  if (expandedId.value !== id) discardConfirmId.value = null
}

const updateStatus = async (lead: Lead, status: string) => {
  updating.value = lead.id
  try {
    const res = await apiFetch(`/admin/contact-leads/${lead.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
    if (res.ok) lead.status = status as Lead['status']
  } finally {
    updating.value = null
  }
}

const archiveLead = async (lead: Lead, archived: boolean) => {
  updating.value = lead.id
  try {
    const res = await apiFetch(`/admin/contact-leads/${lead.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived }),
    })
    if (res.ok) {
      // The lead no longer belongs in the current view — drop it locally.
      leads.value = leads.value.filter(l => l.id !== lead.id)
      total.value = Math.max(total.value - 1, 0)
      selected.value.delete(lead.id)
      expandedId.value = null
    }
  } finally {
    updating.value = null
  }
}

const discardLead = async (lead: Lead) => {
  discarding.value = lead.id
  try {
    const res = await apiFetch(`/admin/contact-leads/${lead.id}`, { method: 'DELETE' })
    if (res.ok) {
      leads.value = leads.value.filter(l => l.id !== lead.id)
      total.value = Math.max(total.value - 1, 0)
      selected.value.delete(lead.id)
      expandedId.value = null
      discardConfirmId.value = null
    }
  } finally {
    discarding.value = null
  }
}

// ── Bulk actions ─────────────────────────────────────────────────────────────
const selectedLeads = () => leads.value.filter(l => selected.value.has(l.id))

const bulkStatus = async (status: Lead['status']) => {
  const targets = selectedLeads()
  if (targets.length === 0) return
  bulkBusy.value = true
  try {
    const results = await Promise.allSettled(
      targets.map(l =>
        apiFetch(`/admin/contact-leads/${l.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        })
      )
    )
    targets.forEach((l, i) => {
      const r = results[i]
      if (r.status === 'fulfilled' && r.value.ok) l.status = status
    })
    clearSelection()
  } finally {
    bulkBusy.value = false
  }
}

const bulkArchive = async (archived: boolean) => {
  const targets = selectedLeads()
  if (targets.length === 0) return
  bulkBusy.value = true
  try {
    const results = await Promise.allSettled(
      targets.map(l =>
        apiFetch(`/admin/contact-leads/${l.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ archived }),
        })
      )
    )
    const movedIds = new Set<number>()
    targets.forEach((l, i) => {
      const r = results[i]
      if (r.status === 'fulfilled' && r.value.ok) movedIds.add(l.id)
    })
    leads.value = leads.value.filter(l => !movedIds.has(l.id))
    total.value = Math.max(total.value - movedIds.size, 0)
    clearSelection()
    // If the page is now empty but earlier pages exist, step back one page.
    if (leads.value.length === 0 && offset.value > 0) reload(offset.value - limit.value)
  } finally {
    bulkBusy.value = false
  }
}

const bulkDelete = async () => {
  const targets = selectedLeads()
  if (targets.length === 0) return
  bulkBusy.value = true
  try {
    const results = await Promise.allSettled(
      targets.map(l => apiFetch(`/admin/contact-leads/${l.id}`, { method: 'DELETE' }))
    )
    const deletedIds = new Set<number>()
    targets.forEach((l, i) => {
      const r = results[i]
      if (r.status === 'fulfilled' && r.value.ok) deletedIds.add(l.id)
    })
    leads.value = leads.value.filter(l => !deletedIds.has(l.id))
    total.value = Math.max(total.value - deletedIds.size, 0)
    expandedId.value = null
    clearSelection()
    if (leads.value.length === 0 && offset.value > 0) reload(offset.value - limit.value)
  } finally {
    bulkBusy.value = false
  }
}
</script>

<style scoped>
/* Loading skeleton */
.leads-loading { display: flex; flex-direction: column; gap: 0.5rem; }
.loading-row {
  height: 42px;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, var(--bg-surface) 25%, var(--bg-surface-hover) 50%, var(--bg-surface) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }

/* View tabs (Active | Archived) */
.cm-viewtabs {
  display: inline-flex;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  overflow: hidden;
}
.cm-tab {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 0.82rem;
  padding: 0.4rem 0.85rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.cm-tab.active { background: var(--accent-dim); color: var(--text-primary); }
.cm-tab:hover:not(.active) { background: var(--bg-surface-hover); }

/* Bulk action bar */
.cm-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  margin-bottom: 0.75rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  min-height: 48px;
  flex-wrap: wrap;
}
.cm-selectall {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.cm-selectall input { cursor: pointer; }
.cm-bar-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }

/* Pagination */
.cm-pager {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  white-space: nowrap;
}
.cm-pageinfo { color: var(--text-muted); font-size: 0.8rem; }
.cm-pagesize {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: var(--text-muted);
  white-space: nowrap;
}
.filter-select-sm { padding: 0.3rem 0.5rem; font-size: 0.8rem; }

/* Container */
.cm-list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--bg-surface);
}

.cm-row { border-bottom: 1px solid var(--border-subtle); }
.cm-row:last-child { border-bottom: none; }
.cm-row.expanded { background: var(--bg-surface-active); }
.cm-row.selected { background: var(--accent-dim); }

/* Compact summary row */
.cm-summary {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 1rem;
  user-select: none;
  min-width: 0;
  background: transparent;
  cursor: pointer;
}
.cm-summary:hover { background: var(--bg-surface-hover); }

.cm-check { cursor: pointer; flex-shrink: 0; }
.cm-chevron { color: var(--text-muted); font-size: 0.7rem; flex-shrink: 0; width: 10px; cursor: pointer; }

/* Status dot */
.status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; cursor: pointer; }
.dot-new       { background: var(--status-new-text); }
.dot-contacted { background: var(--status-contacted-text); }
.dot-converted { background: var(--status-converted-text); }
.dot-closed    { background: var(--text-disabled); }

.cm-name    { font-weight: 600; font-size: 0.875rem; color: var(--text-primary); white-space: nowrap; flex-shrink: 0; cursor: pointer; }
.cm-email   { color: var(--accent-text); font-size: 0.8rem; text-decoration: none; white-space: nowrap; flex-shrink: 0; }
.cm-email:hover { text-decoration: underline; }
.cm-service { color: var(--text-muted); font-size: 0.78rem; white-space: nowrap; flex-shrink: 0; cursor: pointer; }

.cm-preview {
  color: var(--text-secondary);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.cm-spacer { flex: 1; min-width: 0; cursor: pointer; }
.cm-date   { color: var(--text-muted); font-size: 0.75rem; white-space: nowrap; flex-shrink: 0; }

/* Expanded detail */
.cm-detail {
  padding: 0.75rem 1rem 0.9rem 2.5rem;
  border-top: 1px solid var(--border-subtle);
}

.cm-message-text {
  color: var(--text-primary);
  font-size: 0.875rem;
  line-height: 1.65;
  margin: 0 0 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--bg-base);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  padding: 0.75rem 0.9rem;
  cursor: pointer;
}

.cm-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }

.btn-discard {
  background: var(--danger-dim);
  color: var(--danger-text);
  border: 1px solid var(--danger-dim);
  border-radius: var(--radius-sm);
  padding: 0.25rem 0.75rem;
  font-size: 0.78rem;
  cursor: pointer;
  margin-left: auto;
}
.btn-discard:hover { opacity: 0.85; }

.discard-confirm-label { color: var(--text-secondary); font-size: 0.8rem; }
.cm-actions .discard-confirm-label { margin-left: auto; }

.btn-discard-confirm {
  background: var(--danger);
  color: var(--text-primary);
  border: none;
  border-radius: var(--radius-sm);
  padding: 0.25rem 0.75rem;
  font-size: 0.78rem;
  cursor: pointer;
}
.btn-discard-confirm:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-discard-confirm:hover:not(:disabled) { opacity: 0.88; }
</style>
